import type { UIAuditCategory } from "@/packages/types";
import { ClipboardList } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";
import { useTranslation } from "@/packages/utils";

interface RecentChecklistCardProps {
  categories: UIAuditCategory[];
  onNavigateToItem: (itemId: string) => void;
  onGoToChecklist: () => void;
  index?: number;
  className?: string;
}

interface FlatItem {
  id: string;
  name: string;
  status: string;
  categoryName: string;
  lastModified: number;
}

function StatusDot({ status }: { status: string }) {
  if (status === "APPROVED") {
    return <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />;
  }
  if (status === "UPLOADED") {
    return (
      <span className="w-2.5 h-2.5 rounded-full border-2 border-accent flex items-center justify-center">
        <span className="w-1 h-1 rounded-full bg-amber-400" />
      </span>
    );
  }
  return <span className="w-2.5 h-2.5 rounded-full border-2 border-border" />;
}

function flattenAndSort(categories: UIAuditCategory[]): FlatItem[] {
  const items: FlatItem[] = [];
  for (const cat of categories) {
    for (const item of cat.items) {
      items.push({
        id: item.id,
        name: item.name,
        status: item.status,
        categoryName: cat.name,
        lastModified: item.lastModified ?? 0,
      });
    }
  }

  const hasModified = items.some((i) => i.lastModified > 0);
  if (hasModified) {
    items.sort((a, b) => b.lastModified - a.lastModified);
  }

  return items.slice(0, 50);
}

export function RecentChecklistCard({
  categories,
  onNavigateToItem,
  onGoToChecklist,
  index,
  className,
}: RecentChecklistCardProps) {
  const { t } = useTranslation("audits");
  const items = flattenAndSort(categories);

  return (
    <WidgetCard
      size="auto"
      title={t("overview.recentChecklist.title")}
      icon={<ClipboardList size={16} />}
      action={
        <button
          type="button"
          className="text-xs font-medium text-accent hover:text-accent/80"
          onClick={onGoToChecklist}
        >
          {t("overview.recentChecklist.goToChecklist")}
        </button>
      }
      index={index}
      className={className}
    >
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full gap-1">
          <ClipboardList size={32} className="text-muted" />
          <span className="text-sm text-muted">
            {t("overview.recentChecklist.empty")}
          </span>
        </div>
      ) : (
        <div className="flex flex-col min-h-0 h-full">
          {/* Header row */}
          <div className="grid grid-cols-[28px_1fr_1fr_120px] items-center py-1.5 text-xs text-muted uppercase tracking-wide border-b border-border/30 pb-1 shrink-0">
            <span />
            <span>{t("overview.recentChecklist.columnItem")}</span>
            <span className="hidden md:block">
              {t("overview.recentChecklist.columnCategory")}
            </span>
            <span className="text-right">
              {t("overview.recentChecklist.columnLastModified")}
            </span>
          </div>

          {/* Data rows — scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto slim-scroll">
            {items.map((item, i) => (
              <button
                type="button"
                key={item.id}
                className={`grid grid-cols-[28px_1fr_1fr_120px] items-center py-1.5 border-b border-border/50 last:border-0 cursor-pointer rounded-md text-left transition-all duration-150 hover:pl-2 hover:border-l-2 hover:border-l-accent/40 w-full ${
                  i % 2 === 1 ? "bg-surface/20" : ""
                }`}
                onClick={() => onNavigateToItem(item.id)}
              >
                <span className="flex items-center justify-center">
                  <StatusDot status={item.status} />
                </span>
                <span className="text-sm text-secondary truncate pr-2">
                  {item.name}
                </span>
                <span className="text-xs text-muted truncate hidden md:block pr-2">
                  {item.categoryName}
                </span>
                <span className="text-xs text-muted text-right">
                  {item.lastModified > 0
                    ? formatDistanceToNow(new Date(item.lastModified), {
                        addSuffix: true,
                      })
                    : "--"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

export function RecentChecklistCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="auto" index={index} />;
}
